/**
 * The cached session: the one piece of state that decides whether this app
 * works at a booth.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE RULE, IN ONE PLACE
 * ─────────────────────────────────────────────────────────────────────────
 * `applySessionResult()` below is the ONLY function permitted to move the app
 * out of `authenticated`, and it does so for exactly one input:
 * `{ kind: 'unauthenticated' }` — which ../api/http.ts produces only for a
 * 401, a 403, or Better Auth's 200-with-`null`-body. Every other failure
 * (`fetch` rejected, our own timeout, any 5xx) arrives as
 * `{ kind: 'offline' }` and leaves the session untouched.
 *
 * If you are about to add a branch that clears the session, it belongs in that
 * function or nowhere. Spreading this decision across call sites is how it
 * gets got backwards, and both directions are real failures: strand the sales
 * team at a login screen in a dead zone, or let a revoked handset keep working.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * BOOT ORDER
 * ─────────────────────────────────────────────────────────────────────────
 * Boot reads IndexedDB FIRST and renders from it, then revalidates over the
 * network in the background. It deliberately does not await the network: a
 * device in airplane mode would otherwise sit on a splash screen for the full
 * request deadline before showing an app it could have shown instantly.
 */
import { create } from 'zustand';

import {
  clearAllLocalData,
  clearCachedSession,
  countUnsyncedWork,
  readCachedSession,
  writeCachedSession
} from '@/lib/db';

import { fetchSession, signInWithEmail, signOutOnServer, type SessionPayload } from './auth-client';
import type { ApiResult } from '@/lib/api/http';

export type SessionStatus =
  /** Boot has not finished reading IndexedDB yet. Render nothing routing-relevant. */
  | 'loading'
  /** A session is in force. It may never have been confirmed this run — see `isStale`. */
  | 'authenticated'
  /** The server said so, the cached session expired, or there was never one. */
  | 'unauthenticated';

/** Whether the last server contact attempt reached the server. */
export type Connectivity = 'unknown' | 'online' | 'offline';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: string | null;
}

interface SessionState {
  status: SessionStatus;
  user: SessionUser | null;
  /** Epoch ms; Better Auth's `session.expiresAt`. Null when unauthenticated. */
  expiresAt: number | null;
  currentEventId: string | null;
  connectivity: Connectivity;
  /** Epoch ms of the last successful `/get-session`, or null if never this run. */
  lastConfirmedAt: number | null;
  /**
   * True when the app is running on a session mirror that has not been
   * confirmed by the server since boot. The sync/pending badge will want this.
   */
  isStale: boolean;

  boot: () => Promise<void>;
  revalidate: () => Promise<void>;
  signIn: (input: { email: string; password: string }) => Promise<SignInOutcome>;
  signOut: (options?: { discardUnsyncedWork?: boolean }) => Promise<SignOutOutcome>;
  setCurrentEventId: (eventId: string | null) => Promise<void>;
}

export type SignInOutcome =
  | { kind: 'success' }
  /** Wrong email OR wrong password — deliberately not distinguished. */
  | { kind: 'invalid-credentials' }
  /** Better Auth's 429. The user must wait, not retype. */
  | { kind: 'rate-limited' }
  /** Could not reach the server. Sign-in is impossible until connectivity returns. */
  | { kind: 'offline' }
  | { kind: 'server-error'; message: string };

export type SignOutOutcome =
  | { kind: 'success' }
  /**
   * Refused: there is unsynced work that exists nowhere but this device.
   * The caller must re-invoke with `discardUnsyncedWork: true` after an
   * explicit confirmation naming the count.
   */
  | { kind: 'needs-confirmation'; unsyncedCount: number };

function expiresAtMs(payload: SessionPayload): number {
  return Date.parse(payload.session.expiresAt);
}

/** Resolves once `boot()` has settled, so route guards can await a real answer. */
let bootSettled: (() => void) | null = null;
const bootComplete = new Promise<void>(resolve => {
  bootSettled = resolve;
});

export function waitForSessionBoot(): Promise<void> {
  return bootComplete;
}

export const useSessionStore = create<SessionState>((set, get) => {
  /**
   * ─────────────────────────────────────────────────────────────────────
   * THE DECISION POINT. Every network answer about identity passes here.
   * ─────────────────────────────────────────────────────────────────────
   */
  async function applySessionResult(result: ApiResult<SessionPayload>): Promise<void> {
    switch (result.kind) {
      case 'ok': {
        const expires = expiresAtMs(result.data);
        const user: SessionUser = {
          id: result.data.user.id,
          email: result.data.user.email,
          name: result.data.user.name,
          role: result.data.user.role ?? null
        };
        await writeCachedSession({
          userId: user.id,
          email: user.email,
          name: user.name,
          role: user.role,
          currentEventId: get().currentEventId,
          expiresAt: expires
        });
        set({
          status: 'authenticated',
          user,
          expiresAt: expires,
          connectivity: 'online',
          lastConfirmedAt: Date.now(),
          isStale: false
        });
        return;
      }

      case 'unauthenticated': {
        // The server was reached and rejected this identity: an expired
        // session, or one revoked from the admin path. This is the ONLY path
        // that discards a session.
        //
        // Note what is NOT done here: the rest of the local database is left
        // alone. A lapsed session is not consent to destroy leads captured at
        // a booth that have not synced anywhere. Wiping everything is
        // `clearAllLocalData()`, reached only from a deliberate sign-out.
        await clearCachedSession();
        set({
          status: 'unauthenticated',
          user: null,
          expiresAt: null,
          connectivity: 'online',
          lastConfirmedAt: null,
          isStale: false
        });
        return;
      }

      case 'offline': {
        // Network error, our own timeout, or a 5xx. Says nothing about
        // identity. Keep whatever session we have; only note connectivity.
        // If boot found no cache at all, status stays `unauthenticated` and
        // the login screen reads `connectivity` to explain that the problem is
        // signal, not the password.
        set({ connectivity: 'offline' });
        return;
      }

      case 'error': {
        // Reached the server; it refused for a non-identity reason (rate
        // limit, or a malformed session body we could not parse). Not a
        // logout, and not offline either.
        set({ connectivity: 'online' });
        return;
      }
    }
  }

  return {
    status: 'loading',
    user: null,
    expiresAt: null,
    currentEventId: null,
    connectivity: 'unknown',
    lastConfirmedAt: null,
    isStale: false,

    async boot() {
      try {
        const cached = await readCachedSession();

        if (cached && cached.expiresAt > Date.now()) {
          // Optimistic, and intentionally so: render the app from the mirror
          // immediately. Revalidation below will correct this within a second
          // if we have signal, and will correctly leave it alone if we do not.
          set({
            status: 'authenticated',
            user: {
              id: cached.userId,
              email: cached.email,
              name: cached.name,
              role: cached.role
            },
            expiresAt: cached.expiresAt,
            currentEventId: cached.currentEventId,
            isStale: true
          });
        } else {
          if (cached) {
            // Locally verifiable death: the token's own horizon has passed, so
            // the server would reject it anyway and no round trip is needed to
            // know that. Drop the mirror — but, as above, not the rest of the
            // database, which may hold unsynced leads.
            await clearCachedSession();
          }
          set({ status: 'unauthenticated', user: null, expiresAt: null, isStale: false });
        }
      } catch {
        // IndexedDB unavailable (private mode, corrupted store). We cannot
        // prove anything about the session, so fall back to unauthenticated
        // and let the network decide.
        set({ status: 'unauthenticated', user: null, expiresAt: null, isStale: false });
      } finally {
        bootSettled?.();
      }

      // Deliberately not awaited: boot must not block on the network.
      void get().revalidate();
    },

    async revalidate() {
      await applySessionResult(await fetchSession());
    },

    async signIn(input) {
      const result = await signInWithEmail(input);

      switch (result.kind) {
        case 'ok':
          break;
        case 'unauthenticated':
          // 401 INVALID_EMAIL_OR_PASSWORD. Note this does NOT go through
          // applySessionResult: a failed sign-in attempt is not a revoked
          // session, and there is nothing cached to clear.
          return { kind: 'invalid-credentials' };
        case 'offline':
          set({ connectivity: 'offline' });
          return { kind: 'offline' };
        case 'error':
          set({ connectivity: 'online' });
          if (result.code === 'RATE_LIMITED') return { kind: 'rate-limited' };
          return { kind: 'server-error', message: result.message };
      }

      // Sign-in's own response carries no `session` object, so it cannot tell
      // us `expiresAt` — the horizon the whole offline cache is measured
      // against. Fetch it. See signInWithEmail's doc comment.
      const session = await fetchSession();
      await applySessionResult(session);

      if (get().status !== 'authenticated') {
        return {
          kind: 'server-error',
          message: 'Signed in, but the session could not be read back. Please try again.'
        };
      }
      return { kind: 'success' };
    },

    async signOut(options = {}) {
      // ───────────────────────────────────────────────────────────────────
      // THE UNSYNCED-WORK SEAM.
      // `countUnsyncedWork()` returns null while the outbox does not exist
      // (this task predates it) — that is "cannot know", and today it is also
      // genuinely "cannot exist", so signing out proceeds. When the
      // offline-core task registers a real counter, this guard starts firing
      // and the caller MUST show a confirmation naming the count before
      // re-invoking with `discardUnsyncedWork: true`. One tap must not be able
      // to destroy a booth's worth of leads that exist nowhere else.
      // ───────────────────────────────────────────────────────────────────
      if (!options.discardUnsyncedWork) {
        const unsyncedCount = await countUnsyncedWork();
        if (unsyncedCount !== null && unsyncedCount > 0) {
          return { kind: 'needs-confirmation', unsyncedCount };
        }
      }

      // Best effort. If the device is offline the server session survives
      // until it expires, but the local wipe below is the part that matters
      // for a handset that may be about to leave the user's hands.
      await signOutOnServer();

      // The whole database, not just the session row. See clearAllLocalData().
      await clearAllLocalData();

      set({
        status: 'unauthenticated',
        user: null,
        expiresAt: null,
        currentEventId: null,
        lastConfirmedAt: null,
        isStale: false
      });
      return { kind: 'success' };
    },

    async setCurrentEventId(eventId) {
      set({ currentEventId: eventId });
      const cached = await readCachedSession();
      if (!cached) return;
      await writeCachedSession({ ...cached, currentEventId: eventId });
    }
  };
});

/**
 * Revalidate when the browser thinks connectivity returned, and when the user
 * comes back to the tab. Both are cheap and both are how a device that was in
 * a dead zone learns its session was revoked while it was away.
 *
 * `navigator.onLine` is only ever used as a *hint to retry* — never as the
 * source of truth for whether we are offline. It reports "online" for a phone
 * associated with a captive-portal hall wifi that routes nowhere, which is the
 * single most common tradeshow network. The authority is what `fetch` actually
 * did, in ../api/http.ts.
 */
export function startSessionWatchers(): () => void {
  const revalidate = (): void => {
    if (useSessionStore.getState().status === 'loading') return;
    void useSessionStore.getState().revalidate();
  };
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') revalidate();
  };

  window.addEventListener('online', revalidate);
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    window.removeEventListener('online', revalidate);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
